import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import {
  useCreateOfferTempMutation,
  useUpdateOfferTempMutation,
} from "../features/hiringstages/offerApiSlice";

import { useFormik } from "formik";
import * as yup from "yup";

export const AddOfferTemplate = ({ goBack, formData, setFormData }) => {
  const [createOfferTemp] = useCreateOfferTempMutation();
  const [updateOfferTemp] = useUpdateOfferTempMutation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNotification, setShowNotification] = useState(false);

  /* -------------------- Drag Components -------------------- */
  const [components] = useState({
    candidateName: "Candidate Name",
    designation: "Designation",
    salary: "Salary",
    joiningDate: "Joining Date",
    signature: "Signature",
  });

  /* -------------------- Yup Validation -------------------- */
  // const validationSchema = yup.object({
  //   offerTitle: yup
  //     .string()
  //     // .trim()
  //     .required("Offer title is required")
  //     .test(
  //       "not-only-spaces",
  //       "Offer title cannot be empty spaces",
  //       (value) => value && value.trim().length > 0
  //     ),

  //   offerDescription: yup
  //     .string()
  //     // .trim()
  //     .required("Offer description is required")
  //     .test(
  //       "not-only-spaces",
  //       "Offer description cannot be empty spaces",
  //       (value) => value && value.trim().length > 0
  //     ),
  // });
  const validationSchema = yup.object({
  offerTitle: yup
    .string()
    .required("Offer title is required")
    .test(
      "not-only-spaces",
      "Offer title cannot be empty spaces",
      (value) => value && value.trim().length > 0
    ),

  offerDescription: yup
    .string()
    .required("Offer description is required")
    .test(
      "valid-template",
      "Please enter a valid template with placeholders",
      (value) => {
        if (!value) return false;

        const trimmed = value.trim();

        //reject very small/random input like "abc"
        if (trimmed.length < 10) return false;

        //check if it contains at least one placeholder ["..."]
        const hasPlaceholder = /\[".*?"\]/.test(trimmed);

        return hasPlaceholder;
      }
    ),
});

  /* -------------------- Formik -------------------- */
  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      offerTitle: formData.offerTitle || "",
      offerDescription: formData.offerDescription || "",
    },

    validationSchema,

    onSubmit: async (values) => {
      setIsSubmitting(true);

      try {
        const payload = {
          ...formData,
          offerTitle: values.offerTitle.trim(),
          offerDescription: values.offerDescription.trim(),
        };

        if (formData.offerId) {
          await updateOfferTemp(payload);
        } else {
          await createOfferTemp(payload);
        }

        setShowNotification(true);
        goBack();
      } catch (error) {
        console.error("Error submitting form:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  /* -------------------- Drag Logic -------------------- */
  const handleDragStart = (e, componentValue) => {
    e.dataTransfer.setData("text/plain", componentValue);
  };

  const handleDrop = (e) => {
    e.preventDefault();

    const draggedText = e.dataTransfer.getData("text/plain");

    const updatedDescription =
      formik.values.offerDescription + `["${draggedText}"]`;

    formik.setFieldValue("offerDescription", updatedDescription);
  };

  const handleDragOver = (e) => e.preventDefault();

  /* -------------------- UI -------------------- */
  return (
    <>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm font-bold text-gray-900 sm:text-xl">
          {formData.offerId ? "Edit Offer Template" : "Offer Template"}
        </p>

        <Link onClick={goBack} className="flex items-center mr-6">
          <ArrowLeftIcon className="mr-2 h-5 w-5" />
          Back
        </Link>
      </div>

      <div className="flex w-full h-96">
        {/* ---------------- FORM ---------------- */}
        <div className="flex w-3/4 h-[570px] bg-white shadow-lg gap-4 ml-5 rounded-lg mt-5">
          <form className="w-full" onSubmit={formik.handleSubmit}>
            {/* Title */}
            <div className="pl-6 pt-3">
              <label className="block text-lg font-medium px-2">
                Offer Template Title:
              </label>

              <input
                type="text"
                name="offerTitle"
                className="mt-1 w-72 rounded-md py-2 px-3 border"
                placeholder="Offer Template Title"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.offerTitle}
              />

              {formik.touched.offerTitle &&
                formik.errors.offerTitle && (
                  <p className="text-red-500 text-sm">
                    {formik.errors.offerTitle}
                  </p>
                )}
            </div>

            {/* Description */}
            <div className="px-6 pt-3">
              <label className="block text-lg font-medium px-2">
                Offer Template Description:
              </label>

              <textarea
                name="offerDescription"
                rows="15"
                className="border w-full rounded-md py-2 px-3"
                placeholder="Enter description here"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.offerDescription}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              />

              {formik.touched.offerDescription &&
                formik.errors.offerDescription && (
                  <p className="text-red-500 text-sm">
                    {formik.errors.offerDescription}
                  </p>
                )}
            </div>

            {/* Submit */}
            <div className="flex justify-end my-3 mx-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-3xl bg-violet-900 py-2 px-6 text-white hover:bg-violet-700"
              >
                {formData.offerId ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>

        {/* ---------------- DRAG ITEMS ---------------- */}
        <div className="flex w-1/4 bg-white shadow-lg ml-5 rounded-lg mt-5">
          <div className="grid grid-cols-3 gap-4 my-5 place-items-center w-full">
            {Object.values(components).map((item) => (
              <input
                key={item}
                value={item}
                disabled
                draggable
                onDragStart={(e) => handleDragStart(e, item)}
                className="bg-inputbg p-4 col-span-3 text-lg font-semibold rounded-md shadow-lg h-10 w-48"
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};